import type { FetchArgType } from "openapi-typescript-fetch";
import type {
    GetListParams,
    GetListResult,
    GetManyReferenceParams,
    GetManyReferenceResult,
    UpdateParams,
    UpdateManyParams,
    CreateParams,
    DeleteManyParams,
    DeleteManyResult,
    UpdateResult,
    GetManyResult,
    CreateResult
} from "react-admin";
import { isEmpty } from "lodash";

import { fetcher } from "../../data";
import { assertIsDefined } from "../../utils/assertions";
import type { BaseFilters } from "../../data/BaseFilters";
import { NotImplementedError } from "../../utils/NotImplementedError";
import type { ErrorDetails, LangParams, MpmDataProvider, MpmDataProviderOperationName } from "../../data";

import { convertIdFilter } from "../../utils/filterUtils";

import type { FrontUserStatisticDTO } from "./FrontUserStatisticDTO";
import type { FrontUserStatisticRecord } from "./FrontUserStatisticRecord";
import { arrayRange } from "../../utils/array";

const MAX_PAGE_SIZE = 500;

const findAllTranslated = fetcher.path("/api/statistic/front-user/findAll").method("post").create();

/**
 * Parses the DTO from backend to record for frontend
 * @param dto
 * @returns UserRecord
 */

function mapDTOToRecord(dto: FrontUserStatisticDTO): FrontUserStatisticRecord {
    // DTO from backend
    const { accountUuid, lastLoginAt, lastConsumptionAt, createdAt, deletedAt, archived, username, email } = dto;

    // Record for front-end
    return {
        id: accountUuid,
        accountId: accountUuid,
        email,
        lastLoginAt,
        deletedAt,
        lastConsumptionAt,
        createdAt,
        username,
        archived
    } as FrontUserStatisticRecord;
}

async function getPage(parameters: FetchArgType<typeof findAllTranslated>): Promise<GetListResult<FrontUserStatisticRecord>> {
    const { data } = await findAllTranslated(parameters);
    const dataPage = data.content;
    assertIsDefined(dataPage);
    const total = dataPage.totalElements;
    assertIsDefined(total);
    assertIsDefined(dataPage.content);

    return {
        data: dataPage.content.map((content) => mapDTOToRecord({ id: 0, ...content })),
        total
    };
}

interface FrontUserStatisticFilters extends BaseFilters {
    fullText?: string;
    dateBegin?: string;
    dateEnd?: string;
    testAccount?: boolean;
    archived?: boolean;
}

function convertFilters(filters: FrontUserStatisticFilters): Omit<FrontUserStatisticFilters, "id"> {
    const newFilters = { ...filters };
    return convertIdFilter(newFilters);
}

async function getList(params: Readonly<GetListParams>): Promise<GetListResult<FrontUserStatisticRecord>> {
    const { ...filters } = params.filter as FrontUserStatisticFilters;

    const parameters: FetchArgType<typeof findAllTranslated> = {
        filter: convertFilters(filters),
        pageable: {
            page: params.pagination.page - 1,
            size: params.pagination.perPage,
            sort: isEmpty(params.sort.field) ? [] : [`${params.sort.field},${params.sort.order}`]
        }
    } as const;

    const { pageable } = parameters;
    const origPageSize = pageable?.size ?? 0;

    if (pageable?.page === 0 && origPageSize > MAX_PAGE_SIZE) {
        // Use pagination
        const pageIds = arrayRange(pageable.page, origPageSize / MAX_PAGE_SIZE, 1);
        const pages = await Promise.all(
            pageIds.map(async (pageId) => {
                const newParameters: FetchArgType<typeof findAllTranslated> = {
                    filter: parameters.filter ?? {},
                    pageable: {
                        page: pageId,
                        size: MAX_PAGE_SIZE,
                        sort: parameters.pageable?.sort ?? []
                    }
                };
                return getPage(newParameters);
            })
        );

        const allData = [];

        for (const page of pages) {
            allData.push(...page.data);
        }

        return {
            data: allData.slice(0, origPageSize),
            total: pages[0]?.total ?? 0
        };
    }

    const { data } = await findAllTranslated(parameters);
    const dataPage = data.content;
    assertIsDefined(dataPage);
    const total = dataPage.totalElements;
    assertIsDefined(total);
    assertIsDefined(dataPage.content);

    return {
        data: dataPage.content.map((content) => mapDTOToRecord({ id: 0, ...content })),
        total
    };
}

async function getOne(_params: LangParams | (number | string)): Promise<FrontUserStatisticRecord> {
    return Promise.reject(new NotImplementedError("_getOne"));
}

async function getMany(_uuids: readonly (number | string)[]): Promise<GetManyResult<FrontUserStatisticRecord>> {
    return Promise.reject(new NotImplementedError("getMany"));
}

async function getManyReference(_params: Readonly<GetManyReferenceParams>): Promise<GetManyReferenceResult<FrontUserStatisticRecord>> {
    return Promise.reject(new NotImplementedError("getManyReference"));
}

async function update(_params: Readonly<UpdateParams<FrontUserStatisticRecord>>): Promise<UpdateResult<FrontUserStatisticRecord>> {
    return Promise.reject(new NotImplementedError("update"));
}

async function updateMany(_params: Readonly<UpdateManyParams>): Promise<string[]> {
    return Promise.reject(new NotImplementedError("updateMany"));
}

async function create(_params: Readonly<CreateParams>): Promise<CreateResult<FrontUserStatisticRecord>> {
    return Promise.reject(new NotImplementedError("create"));
}

// eslint-disable-next-line no-underscore-dangle
async function _delete(_uuid: number | string): Promise<FrontUserStatisticRecord> {
    return Promise.reject(new NotImplementedError("delete"));
}

async function deleteMany(_params: Readonly<DeleteManyParams>): Promise<DeleteManyResult> {
    return Promise.reject(new NotImplementedError("deleteMany"));
}

function getErrorDetails(_error: unknown, _operation: MpmDataProviderOperationName<FrontUserStatisticRecord>): ErrorDetails | undefined {
    // eslint-disable-next-line no-undefined
    return undefined;
}

export const FrontUserStatisticDataProvider: MpmDataProvider<FrontUserStatisticRecord> = {
    create,
    delete: _delete,
    deleteMany,
    getErrorDetails,
    getList,
    getMany,
    getManyReference,
    getOne,
    update,
    updateMany
};
